/**
 * passkey.js — iter195. Thin wrappers around WebAuthn's navigator.credentials
 * API + the backend /api/auth/passkey/* endpoints.
 *
 * The library converts between base64url (server JSON) and ArrayBuffer (browser
 * API) on the fly. Both flows return the friendly JWT/credential server response.
 */
import api from './api';

// ---------- base64url <-> ArrayBuffer ----------
const b64uToBuf = (b64u) => {
  if (!b64u) return new ArrayBuffer(0);
  const b64 = b64u.replace(/-/g, '+').replace(/_/g, '/').padEnd(b64u.length + (4 - (b64u.length % 4)) % 4, '=');
  const str = atob(b64);
  const buf = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i += 1) buf[i] = str.charCodeAt(i);
  return buf.buffer;
};

const bufToB64u = (buf) => {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (let i = 0; i < bytes.length; i += 1) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

// ---------- support check ----------
export const passkeysSupported = () =>
  typeof window !== 'undefined' &&
  !!window.PublicKeyCredential &&
  !!navigator.credentials?.create &&
  !!navigator.credentials?.get;

export const platformAuthenticatorAvailable = async () => {
  if (!passkeysSupported()) return false;
  try {
    const fn = window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable;
    return typeof fn === 'function' ? await fn() : false;
  } catch {
    return false;
  }
};

// ---------- registration ----------
export async function registerPasskey(deviceName) {
  if (!passkeysSupported()) throw new Error('Passkeys not supported in this browser');

  const { data: options } = await api.post('/auth/passkey/register/begin', { device_name: deviceName });

  const publicKey = {
    ...options,
    challenge: b64uToBuf(options.challenge),
    user: { ...options.user, id: b64uToBuf(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map((c) => ({ ...c, id: b64uToBuf(c.id) })),
  };

  const credential = await navigator.credentials.create({ publicKey });
  if (!credential) throw new Error('Passkey enrollment cancelled');

  const payload = {
    id: credential.id,
    rawId: bufToB64u(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufToB64u(credential.response.clientDataJSON),
      attestationObject: bufToB64u(credential.response.attestationObject),
      transports: credential.response.getTransports?.() || [],
    },
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
    authenticatorAttachment: credential.authenticatorAttachment || null,
  };

  const { data } = await api.post('/auth/passkey/register/finish', {
    credential: payload,
    device_name: deviceName,
  });
  return data;
}

// ---------- authentication (passwordless login) ----------
export async function authenticateWithPasskey({ phone, rememberMe = true } = {}) {
  if (!passkeysSupported()) throw new Error('Passkeys not supported in this browser');

  const beginPayload = phone ? { phone } : {};
  const { data: options } = await api.post('/auth/passkey/auth/begin', beginPayload);

  const publicKey = {
    ...options,
    challenge: b64uToBuf(options.challenge),
    allowCredentials: (options.allowCredentials || []).map((c) => ({ ...c, id: b64uToBuf(c.id) })),
  };

  const assertion = await navigator.credentials.get({ publicKey });
  if (!assertion) throw new Error('Passkey sign-in cancelled');

  const payload = {
    id: assertion.id,
    rawId: bufToB64u(assertion.rawId),
    type: assertion.type,
    response: {
      clientDataJSON: bufToB64u(assertion.response.clientDataJSON),
      authenticatorData: bufToB64u(assertion.response.authenticatorData),
      signature: bufToB64u(assertion.response.signature),
      userHandle: assertion.response.userHandle ? bufToB64u(assertion.response.userHandle) : null,
    },
    clientExtensionResults: assertion.getClientExtensionResults?.() || {},
  };

  const { data } = await api.post('/auth/passkey/auth/finish', {
    credential: payload,
    phone: phone || undefined,
    remember_me: !!rememberMe,
  });
  return data; // { token, user }
}

// ---------- list / delete ----------
export async function listPasskeys() {
  const { data } = await api.get('/auth/passkey/list');
  return data;
}

export async function deletePasskey(id) {
  const { data } = await api.delete(`/auth/passkey/${id}`);
  return data;
}

export async function checkHasPasskey(phone) {
  if (!phone) return false;
  try {
    const { data } = await api.post('/auth/passkey/has-passkey', { phone });
    return !!data?.has_passkey;
  } catch {
    return false;
  }
}
